/**
 * Widgets of the REPSit.ai welcome dashboard (Beta-1 plan P3-20, §1c universal layer).
 * Importing this module registers them; `src/pages/Dashboard.jsx` does that.
 *
 * Tim's D01 copy lives in the components below, verbatim; the hero paragraph and the
 * "Coming to REPSit.ai" section stay on the page itself (they are not dashboard cards).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Chip, LinearProgress, Link, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CelebrationIcon from '@mui/icons-material/Celebration';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { openFeedback } from '../../config/feedback';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUserOrganizations } from '../../assistants/drew/hooks/useOrganization';
import { useOnboarding } from '../../onboarding/OnboardingProvider';
import { ONBOARDING_STEPS, OPEN_WELCOME_EVENT } from '../../onboarding/onboardingEvents';
import { stepTitle } from '../../onboarding/steps';
import { getAssistant } from '../../assistants/assistants';
import { normalizeThresholds, scoreBand } from '../../assistants/drew/utils/bands';
import { useDrewStatus } from './useDrewStatus.js';
import { widgetRegistry, DASHBOARD_KEYS } from '../widgets/widgetRegistry.js';
import { DA } from '../navigation/navigation.js';

const DREW = getAssistant('drew');
const DREW_ROUTE = '/drew';

/* ------------------------------------------------------------------ Access DREW (Tim's D01 text) */

export function AccessDrewCard({ size = 'XL' }) {
  const { hasToolAccess } = useUser();
  const navigate = useNavigate();
  const daActive = hasToolAccess('drift-accumulation');
  const compact = size !== 'XL';
  return (
    <Card sx={{ height: '100%', bgcolor: 'primary.main', color: 'primary.contrastText', position: 'relative', overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2.5, md: compact ? 2.5 : 4 }, height: '100%', display: 'flex', gap: 3, '&:last-child': { pb: { xs: 2.5, md: compact ? 2.5 : 4 } } }}>
      {/* Two real columns: text + button on the left, DREW's portrait on the right — the portrait
          is sized by the card's height so it is never cropped (UI pass 2026-09-21). */}
      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box component="img" src={DREW.avatar} alt="DREW" sx={{ width: compact ? 48 : 64, height: compact ? 48 : 64, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              DREW
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {DREW.shortTitle}
            </Typography>
          </Box>
          <Chip label="ACTIVE" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 700, ml: 0.5 }} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
          <Typography variant="body1" sx={{ opacity: 0.92, mb: 1.25, maxWidth: 640 }}>
            DREW, your Drift &amp; Accumulation Intelligent Assistant, provides for the documentation and remediation
            of Drift in Perception of Risk and Drift in &ldquo;Work As Done&rdquo; vs &ldquo;Work As Imagined,&rdquo; as well as
            of organizational and systematic setups for human error (Traps, Roadblocks, and Landmines), collectively
            known as Organizational Entropy (OE). It also provides the opportunity to survey team members on their
            perspectives regarding drift and setups. It collects and assesses data, intelligently analyzes trends,
            initiates notifications and alerts when warranted, and provides recommendations where appropriate.
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.92, mb: 1.25, maxWidth: 640 }}>
            DREW assesses all input within the context of the four REPS Imperatives: Reliability, Efficiency,
            Productivity, and Safety. Noted conditions and their trends are considered against documented
            organization and industry historical incident patterns. Warnings initiate the moment data crosses its
            pre-determined threshold.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mb: 2, maxWidth: 640 }}>
            During beta, you are &ldquo;sandboxed,&rdquo; which means that, just as in Las Vegas, what happens
            here&hellip;stays here. We have pre-loaded <strong>Magnum, Inc.</strong>, a mock organization with ten
            facilities and 90 days of history. It is yours to play (LEARN) with. If you break it and want a do-over,
            let us know and we&rsquo;ll restore your sandbox to its starting point.
          </Typography>
        </Box>
        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            onClick={() => navigate(DREW_ROUTE)}
            disabled={!daActive}
            endIcon={<ArrowForwardIcon />}
            sx={{ bgcolor: 'common.white', color: 'primary.main', fontWeight: 800, px: 3, '&:hover': { bgcolor: 'grey.100' } }}
          >
            {daActive ? 'Access DREW' : 'Access being set up…'}
          </Button>
        </Box>
      </Box>
      {!compact && (
        <Box sx={{ flex: '0 0 auto', display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', width: { md: '34%', lg: '36%' }, maxWidth: 400 }}>
          <Box
            component="img"
            src={DREW.avatar}
            alt=""
            aria-hidden
            sx={{ width: '100%', height: '100%', maxHeight: '100%', objectFit: 'contain', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.25))' }}
          />
        </Box>
      )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ DREW status (Alex 09-12: "the main function of DREW as a widget") */

const REPS = [
  ['reliability', 'R', 'Reliability'],
  ['efficiency', 'E', 'Efficiency'],
  ['productivity', 'P', 'Productivity'],
  ['safety', 'S', 'Safety'],
];
const TYPES = [
  ['drift', 'Drift'],
  ['trap', 'Traps'],
  ['roadblock', 'Roadblocks'],
  ['landmine', 'Landmines'],
];

/** A headline score: number, band pill, label — one object, not floating digits. */
function HeadlineScore({ value, label, threshold }) {
  const band = scoreBand(value, threshold);
  return (
    <Box sx={{ flex: '1 1 150px', minWidth: 0, p: 1.5, borderRadius: 2, bgcolor: 'action.hover', borderLeft: 4, borderLeftColor: band.color }}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, letterSpacing: 0.8, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 32, lineHeight: 1, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
          {value == null ? '—' : value.toFixed(1)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>/ 10</Typography>
        {value != null && (
          <Chip size="small" label={band.label} sx={{ ml: 'auto', height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: `${band.color}22`, color: band.color }} />
        )}
      </Box>
    </Box>
  );
}

/** One REPS quadrant as a labelled bar — length and colour both say the score. */
function RepsBar({ letter, name, value, threshold }) {
  const band = scoreBand(value, threshold);
  const pct = value == null ? 0 : Math.max(0, Math.min(100, (value / 10) * 100));
  return (
    <Tooltip title={`${name}: ${value == null ? 'no data' : `${value.toFixed(1)} / 10 — ${band.label}`}`}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '16px 1fr 34px', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>{letter}</Typography>
        <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: band.color, borderRadius: 4, transition: 'width 0.3s' }} />
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
          {value == null ? '—' : value.toFixed(1)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * What DREW is for, at a glance: Drift and Accumulation scores (from the latest OE snapshot's
 * element scores), the four REPS scores, open OE Conditions by type and active warnings.
 * The whole card is the shortcut into DREW (C0813) — the shell handles the click.
 */
export function DrewStatusCard({ size = 'M', orgId, thresholds = null }) {
  const navigate = useNavigate();
  const status = useDrewStatus(orgId);
  const tN = normalizeThresholds(thresholds);
  const avgThreshold = (tN.reliability + tN.efficiency + tN.productivity + tN.safety) / 4;
  const wide = size === 'W' || size === 'XL';
  const tall = size === 'L' || size === 'XL';
  const warnings = status.warnings || [];
  const open = status.open || { total: 0 };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box component="img" src={DREW.avatarSmall || DREW.avatar} alt="" sx={{ width: 32, height: 32 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
              DREW · OE status
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {status.loading ? 'Reading the latest snapshot…' : status.hasSnapshot ? 'Latest organization snapshot' : 'No survey snapshot yet'}
            </Typography>
          </Box>
          {warnings.length > 0 && (
            <Tooltip title={warnings.map((w) => w.message || w.quadrant || 'warning').join(' · ')}>
              <Chip size="small" color="error" label={`${warnings.length} warning${warnings.length === 1 ? '' : 's'}`} />
            </Tooltip>
          )}
        </Box>

        {status.loading ? (
          <Skeleton variant="rounded" height={tall ? 140 : 96} sx={{ borderRadius: 2 }} />
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Headline: Drift + Accumulation as two stat blocks */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <HeadlineScore value={status.drift} label="Drift" threshold={avgThreshold} />
              <HeadlineScore value={status.accumulation} label="Accumulation" threshold={avgThreshold} />
            </Box>

            {/* REPS quadrants as bars — 2×2 on Medium, one row on Wide */}
            <Box sx={{ display: 'grid', gridTemplateColumns: wide ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)', columnGap: 2.5, rowGap: 0.75 }}>
              {REPS.map(([key, letter, name]) => (
                <RepsBar key={key} letter={letter} name={name} value={status.reps ? status.reps[key] : null} threshold={tN[key]} />
              ))}
            </Box>

            {/* Footer: open conditions */}
            <Box sx={{ mt: 'auto', pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {open.total > 0 ? (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {open.total} open OE condition{open.total === 1 ? '' : 's'}
                  </Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {TYPES.filter(([key]) => open[key] > 0).map(([key, label]) => (
                      <Chip key={key} size="small" variant="outlined" label={`${label} ${open[key]}`} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} />
                    ))}
                  </Stack>
                </>
              ) : (
                /* Four zero chips read as "broken" to a first-time tester (UI review 2026-09-21 §2) */
                <Typography variant="caption" color="text.secondary">
                  No open OE conditions —{' '}
                  <Link component="button" underline="hover" onClick={(e) => { e.stopPropagation(); navigate(`${DA}/rollup/trl-entry`); }} sx={{ fontSize: 'inherit', verticalAlign: 'baseline', fontWeight: 700 }}>
                    log the first one →
                  </Link>
                </Typography>
              )}
              {tall && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Green &lt; 4 · yellow 4–6 · red &gt; 6
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ Getting started (5 steps) */

export function GettingStartedCard({ size = 'L' }) {
  const { steps } = useOnboarding();
  const doneCount = ONBOARDING_STEPS.filter((k) => steps[k]).length;
  const total = ONBOARDING_STEPS.length;
  const allDone = doneCount === total;
  const nextKey = ONBOARDING_STEPS.find((k) => !steps[k]);
  const showList = size !== 'S';

  return (
    <Card variant="outlined" sx={{ height: '100%', borderColor: allDone ? 'success.light' : 'primary.light' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {allDone ? <CelebrationIcon color="success" /> : <RocketLaunchIcon color="primary" />}
          <Typography variant="subtitle1" fontWeight={800}>
            {allDone ? 'You did the whole loop!' : 'Getting started'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {doneCount}/{total}
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={(doneCount / total) * 100} color={allDone ? 'success' : 'primary'} sx={{ height: 6, borderRadius: 3, mb: 1.5 }} />
        {showList && (
          <Stack spacing={0.5} sx={{ mb: 2, flexGrow: 1, overflow: 'auto' }}>
            {ONBOARDING_STEPS.map((k) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CheckCircleIcon sx={{ fontSize: 16 }} color={steps[k] ? 'success' : 'disabled'} />
                <Typography variant="body2" color={steps[k] ? 'text.primary' : 'text.secondary'}>
                  {stepTitle(k)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        <Box sx={{ mt: 'auto' }}>
          <Button
            variant={allDone ? 'outlined' : 'contained'}
            size="small"
            onClick={() => window.dispatchEvent(new Event(OPEN_WELCOME_EVENT))}
            endIcon={<ArrowForwardIcon />}
          >
            {allDone ? 'Revisit the tours' : nextKey ? `Continue: ${stepTitle(nextKey)}` : 'Open checklist'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ Help shortcuts (Tim: "no changes") */

export function HelpShortcutsCard() {
  const navigate = useNavigate();
  const items = [
    { icon: <HelpOutlineIcon />, title: 'Help & Docs', text: 'Core concepts, how‑do‑I answers and the FAQ, for REPSit.ai and DREW.', onClick: () => navigate('/help') },
    { icon: <MenuBookIcon />, title: 'Getting‑started guide', text: 'The V1 user guide — five short tours and what each screen is for.', onClick: () => window.open('/guide', '_blank', 'noopener') },
    { icon: <EmailOutlinedIcon />, title: 'Tell the Build Team', text: 'Two humans read every note you send. Bugs, ideas, confusion — all welcome.', onClick: () => openFeedback('feedback') },
  ];
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, '&:last-child': { pb: 2 } }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={800}>
            Never explore alone
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Guided tours, docs, and two humans reading every note you send.
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {items.map((it) => (
            <Box
              key={it.title}
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); it.onClick(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); it.onClick(); } }}
              sx={{
                p: 2, borderRadius: 2, bgcolor: 'action.hover', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0.75, justifyContent: 'center',
                transition: 'background-color 0.15s, transform 0.15s', '&:hover': { bgcolor: 'action.selected', transform: 'translateY(-2px)' },
                '&:focus-visible': { outline: (t) => `3px solid ${t.palette.primary.main}55` },
              }}
            >
              <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                {it.icon}
                <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>{it.title}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>{it.text}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ registration */

const REPSIT_ONLY = [DASHBOARD_KEYS.repsit];

function DrewStatusWidget({ size }) {
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  return <DrewStatusCard size={size} orgId={organizations[0]?.id || null} thresholds={organizations[0]?.thresholds || null} />;
}

const DEFS = [
  {
    id: 'drew-status',
    title: 'DREW · OE status',
    description: 'Drift and Accumulation scores, the four REPS scores, open OE Conditions by type and active warnings — click to open DREW.',
    category: 'scores',
    component: DrewStatusWidget,
    sizes: ['M', 'W', 'L', 'XL'],
    defaultSize: 'M',
    href: DREW_ROUTE,
    dashboards: REPSIT_ONLY,
    frameless: true,
    clickable: true,
  },
  {
    id: 'access-drew',
    title: 'Access DREW',
    description: 'Your active Intelligent Assistant — what DREW does and the door to the OE Dashboard.',
    category: 'guidance',
    component: ({ size }) => <AccessDrewCard size={size} />,
    sizes: ['L', 'XL'],
    defaultSize: 'XL',
    href: DREW_ROUTE,
    dashboards: REPSIT_ONLY,
    frameless: true,
  },
  {
    id: 'getting-started',
    title: 'Getting started',
    description: 'Your five-step beta checklist and the next step to take.',
    category: 'guidance',
    component: ({ size }) => <GettingStartedCard size={size} />,
    sizes: ['M', 'L'],
    defaultSize: 'M',
    dashboards: [DASHBOARD_KEYS.repsit, DASHBOARD_KEYS.drewItm],
    frameless: true,
    scroll: true,
  },
  {
    id: 'help-shortcuts',
    title: 'Never explore alone',
    description: 'Help & Docs and the getting-started guide, one click away.',
    category: 'guidance',
    component: () => <HelpShortcutsCard />,
    sizes: ['M', 'W', 'L'],
    defaultSize: 'W',
    href: '/help',
    dashboards: REPSIT_ONLY,
    frameless: true,
  },
];

for (const def of DEFS) widgetRegistry.upsert(def);

export const REPSIT_WIDGET_IDS = DEFS.map((d) => d.id);
